'use client';
import { CalendarPlus01, Check, Edit02, Plus, TypeSquare } from '@ethsign/icons';

import { Button } from '@ethsign/ui';

import classNames from 'classnames';
import { useEffect } from 'react';

import { ContractTypeCheckItem } from '@/components/ContractTypeCheckItem';
import { dragImage } from '@/constants/editor';
import { IField, SendType, SIGN_MODE_ENUM } from '@/types';
import { getNaNoId, getNanoidEn } from '@/utils';

import { EditTermsForm } from './EditTermsForm';
import { SignerItem } from './SignerItem';

interface Props {
  sendType?: SendType;
  isAddTerms?: boolean;
  fields?: IField[];
  selectedItem?: IField;
  onSelectItem?: (item?: IField) => void;
  currentSigner?: any;
  signers?: any[];
  setCurrentSigner?: (item: any) => void;
  changeSigner: (type: 'add' | 'delete' | 'edit', data: any) => void;
  onContractTypeChange?: (type: SIGN_MODE_ENUM) => void;
  contractType?: SIGN_MODE_ENUM;
  isEditTerms?: boolean;
  onFieldChange?: (data: { id: string; value: string }) => void;
}

const colors = [
  { text: '#DC6803', bg: '#FCE4EC' },
  { text: '#D81B60', bg: '#FFFAEB' },
  { text: '#1570EF', bg: '#F5FBEE' },
  { text: '#175CD3', bg: '#EFF8FF' },
  { text: '#088AB2', bg: '#ECFDFF' },
  { text: '#764C97', bg: '#EBE5F0' },
  { text: '#475467', bg: '#F9FAFB' },
  { text: '#D92D20', bg: '#FEF3F2' },
  { text: '#1D2939', bg: '#F2F4F7' }
];
const colorsLen = colors.length - 1;
export function LeftPanel(props: Props) {
  const {
    isEditTerms,
    isAddTerms,
    fields,
    selectedItem,
    onSelectItem,
    signers,
    currentSigner,
    setCurrentSigner,
    changeSigner,
    contractType,
    onContractTypeChange,
    onFieldChange,
    sendType
  } = props;
  const signerIndex = sendType === SendType.invite ? signers?.findIndex((item) => item.id === currentSigner?.id) : 0;

  const index = isAddTerms ? 8 : (signerIndex === undefined ? -1 : signerIndex) % colorsLen;

  const dragImageWidth = isAddTerms ? 66 : 94;
  const isActive = sendType === SendType.invite ? index > -1 : true;
  const isInvite = sendType === 'invite';
  const terms = fields?.filter((item) => item.type === 'term');
  const dragStart = (e: any, type: keyof typeof dragImage) => {
    if (!isActive) return;
    const container = document.createElement('div');
    container.id = 'dragimage-container';
    Object.assign(container.style, {
      opacity: '0.8',
      backgroundImage: `url("${dragImage[type].replaceAll('color', colors[index].text.slice(1))}")`,
      backgroundSize: '100% 100%',
      width: dragImageWidth + 'px',
      height: '26px'
    });
    document.body.appendChild(container);
    e.dataTransfer.setDragImage(container, 0, 13);
    const id = getNanoidEn(6);
    const html = `<span class="signaturecomponent" data-info='${JSON.stringify({
      type,
      id,
      color: colors[index].text.slice(1),
      custom: JSON.stringify(currentSigner),
      value: type !== 'term' ? `{{${type.slice(0, 1)}_${id}}}` : '',
      required: type === 'signature'
    })}' ></span>`;

    e.dataTransfer.setData('text/html', html);
  };

  const onFieldClick = (e: any, type: keyof typeof dragImage) => {
    e.nativeEvent.stopImmediatePropagation();
    if (!isActive) return;
    const dragimage = document.getElementById('dragimage-container');
    if (dragimage) return;
    const container = document.createElement('div');
    container.id = 'dragimage-container';
    Object.assign(container.style, {
      opacity: '0.8',
      backgroundImage: `url("${dragImage[type].replaceAll('color', colors[index].text.slice(1))}")`,
      backgroundSize: '100% 100%',
      width: dragImageWidth + 'px',
      height: '26px',
      position: 'fixed',
      zIndex: '9999',
      transform: `translate3D(${e.clientX + 2}px,${e.clientY - 13}px,0)`,
      left: '0',
      top: '0'
    });
    const id = getNanoidEn(6);
    container.setAttribute('data-color', colors[index].text.slice(1));
    container.setAttribute('data-type', type);
    container.setAttribute('data-id', id);
    container.setAttribute('data-custom', JSON.stringify(currentSigner));
    container.setAttribute('data-value', type !== 'term' ? `{{${type.slice(0, 1)}_${id}}}` : '');
    container.setAttribute('data-required', type === 'signature' ? 'true' : '');

    document.body.appendChild(container);
  };
  const deleteDragimage = () => {
    const dragimage = document.getElementById('dragimage-container');
    if (dragimage) {
      document.body.removeChild(dragimage as Node);
      onSelectItem && onSelectItem(undefined);
    }
  };
  useEffect(() => {
    const moveFunc = (e: { x: number; y: number }) => {
      const dragimage = document.getElementById('dragimage-container');
      if (!dragimage) return;
      dragimage.style.transform = `translate3D(${e.x + 2}px,${e.y - 13}px,0)`;
    };

    document.addEventListener('click', deleteDragimage);
    document.addEventListener('mousemove', moveFunc, false);
    return () => {
      document.removeEventListener('mousemove', moveFunc, false);
      document.removeEventListener('click', deleteDragimage);
    };
  }, []);
  const dragEnd = () => {
    deleteDragimage();
  };
  return (
    <div className="px-5 pt-8 h-full border-r border-gray-200 w-[352px]">
      {!!isAddTerms && (
        <div className="h-full flex flex-col">
          <div className="text-lg font-bold text-gray-600">Add Terms</div>
          <div className="mt-6 mb-3 text-xs font-normal text-gray-500">Terms</div>
          <div
            className="w-full"
            draggable
            onClick={(e) => {
              onFieldClick(e, 'term');
            }}
            onDragStart={(e) => dragStart(e, 'term')}
            onDragEnd={() => dragEnd()}
          >
            <Button className="w-full" variant="outline" size="lg">
              <Plus size={20} color="#344054" />
              <span className="ml-2">Add Terms</span>
            </Button>
          </div>
          <div className="my-3 text-xs font-normal text-gray-500 px-5 py-4 rounded-lg bg-gray-100">
            * Terms are editable for contract preparer before sending out contracts.
          </div>
          <div className=" mb-3 text-xs font-normal text-gray-500">Table of Content</div>
          <div className="flex-1 overflow-auto pb-5">
            {terms?.map((item, i) => {
              const active = selectedItem?.id === item.id;
              return (
                <div
                  className={classNames(
                    'pl-2 text-sm mb-2 text-gray-900 cursor-pointer flex flex-row items-center hover:bg-gray-50 py-2 border border-gray-100 rounded',
                    {
                      'text-primary-900': active
                    }
                  )}
                  key={i}
                  onClick={() => {
                    onSelectItem && onSelectItem(item);
                  }}
                >
                  {active ? <Check size={16} color="#CF5C10" /> : <span className="inline-block w-4"></span>}
                  <span className="ml-2">{item.label || 'Term ' + i}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {!!isEditTerms && (
        <div className="h-full flex flex-col">
          <div className="text-lg font-bold text-gray-600">Edit Terms</div>
          <div className="mt-2 mb-3 text-xs font-normal text-gray-500">
            Please fill in all of the terms to complete the contracts.
          </div>
          <div className="mt-6 mb-3 text-xs font-normal text-gray-500">Terms</div>
          <div className="flex-1 overflow-auto pb-5">
            {terms && terms.length > 0 && (
              <EditTermsForm onFieldClick={onSelectItem} data={terms} onChange={onFieldChange} />
            )}
          </div>
        </div>
      )}
      {!isAddTerms && !isEditTerms && (
        <>
          <div className="text-lg font-bold text-gray-600">Add Fields</div>
          <div className="mt-6 mb-3 text-xs font-normal text-gray-500">
            {isInvite ? 'Signing Parties' : 'Contract Type:'}
          </div>
          {isInvite ? (
            <>
              {signers?.map((item, i) => {
                const active = item.id === currentSigner?.id;
                const hasSignature = fields?.some(
                  (it: any) => it.type === 'signature' && it.custom && JSON.parse(it.custom)?.id === item.id
                );
                const isCompleted = hasSignature;
                return (
                  <SignerItem
                    name={item.name}
                    onEdit={(data) => {
                      changeSigner('edit', data);
                    }}
                    i={i}
                    id={item.id}
                    onClick={() => setCurrentSigner && setCurrentSigner(item)}
                    key={i}
                    isActive={active}
                    isCompleted={isCompleted}
                    onDelete={(id) => changeSigner('delete', { id })}
                  />
                );
              })}
              <div
                className="w-full"
                onClick={() => {
                  changeSigner('add', { id: getNaNoId(), name: 'Party ' + ((signers?.length || 0) + 1) });
                }}
              >
                <Button className="w-full" variant="outline" size="lg">
                  <Plus size={20} color="#344054" />
                  <span className="ml-2">Add Signer</span>
                </Button>
              </div>
            </>
          ) : (
            <div>
              <ContractTypeCheckItem
                checked={contractType === SIGN_MODE_ENUM.OneSignOne}
                onClick={() => onContractTypeChange?.(SIGN_MODE_ENUM.OneSignOne)}
                title="Each signer signs their own contract"
                description="Add signature field to the contract. Everyone receives a separate copy"
              />
              <ContractTypeCheckItem
                className="mt-3"
                checked={contractType === SIGN_MODE_ENUM.AllSignOne}
                onClick={() => onContractTypeChange?.(SIGN_MODE_ENUM.AllSignOne)}
                title="All signatures on one contract"
                description="Signatures will be appended to the bottom of the contract. "
              />
            </div>
          )}

          {!isInvite && contractType === SIGN_MODE_ENUM.AllSignOne ? null : (
            <>
              <div className="mt-6 mb-3 text-xs font-normal text-gray-500">Add Fields</div>
              <div className="flex flex-row flex-wrap" style={{ opacity: isActive ? 1 : 0.6 }}>
                <div
                  onClick={(e) => onFieldClick(e, 'signature')}
                  onDragStart={(e) => dragStart(e, 'signature')}
                  onDragEnd={() => dragEnd()}
                  draggable={isActive}
                  className="pt-[22px] py-[14px] overflow-hidden relative cursor-pointer hover:bg-yellow-25 w-[128px] h-[88px] mb-4 mr-4 flex flex-col justify-center items-center border border-gray-200 rounded-lg"
                >
                  <Edit02 color="#475467" size={20} />
                  <span className="mt-[14px] text-xs text-gray-700 font-medium">Signature Field</span>
                  <span className="bg-tangerine-50 absolute right-0 top-0 text-xxs px-[6px] text-primary-800 rounded-bl-lg">
                    Required
                  </span>
                </div>
                <div
                  draggable={isActive}
                  onClick={(e) => onFieldClick(e, 'date')}
                  onDragStart={(e) => dragStart(e, 'date')}
                  onDragEnd={() => dragEnd()}
                  className="pt-[22px] py-[14px] overflow-hidden relative cursor-pointer hover:bg-yellow-25 w-[128px] h-[88px] mb-4 flex flex-col justify-center items-center border border-gray-200 rounded-lg"
                >
                  <CalendarPlus01 color="#475467" size={20} />
                  <span className="mt-[14px] text-xs text-gray-700 font-medium"> Date Signed</span>
                </div>
                <div
                  onClick={(e) => onFieldClick(e, 'text')}
                  onDragStart={(e) => dragStart(e, 'text')}
                  onDragEnd={() => dragEnd()}
                  draggable={isActive}
                  className="pt-[22px] py-[14px] overflow-hidden relative cursor-pointer hover:bg-yellow-25 w-[128px] h-[88px] flex flex-col justify-center items-center border border-gray-200 rounded-lg"
                >
                  <TypeSquare color="#475467" size={20} />
                  <span className="mt-[14px] text-xs text-gray-700 font-medium">Input Text Field</span>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
